import {
    type ComponentProps,
    type ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { DocType } from '@/models/docs';
import {
    EyeOff,
    Info,
    MessageSquare,
    Plus,
    Scissors,
    TriangleAlert,
    Undo2,
} from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';

import { useReportUnsavedChanges } from '@/hooks/useUnsavedChangesGuard';

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

import { applyUnsureMark } from './CheckedMark';
import { ClaudeUnsureNote, unsurePageKeys } from './ClaudeDraftBanner';
import { CopyAsTextButton } from './CopyAsTextButton';
import { EditorFooter, loadErrorMessage } from './EditorFooter';
import { FileNavBar } from './FileNavBar';
import { FileRemarkBox } from './FileRemarkBox';
import {
    DOCUMENTS_SECTION_ID,
    FILE_SECTION_ID,
    LayerChip,
    OPEN_SECTION_EVENT,
} from './GroundTruthLayerSteps';
import { Hint } from './Hint';
import { HoverNote } from './HoverNote';
import { LoadError } from './LoadError';
import { PanelSkeleton } from './LoadingSkeletons';
import { PageSplitRow, isShowablePage, splitTypeLabel } from './PageSplitRow';
import {
    useCreateGtDocsFromSplits,
    useGtCreateDocsCheck,
    useGtFileDocs,
    useGtFileStatus,
    useGtLeftOutPages,
    useGtPageCount,
    useGtPageSplits,
    useSaveGtLeftOutPages,
    useSaveGtPageSplits,
} from './api';
import { getServerErrorMessage, transientSaveFailure } from './errors';
import { fileDisplayName, pluralize, rangeLabel } from './fileLabels';
import { QUIET_TOAST } from './hubToast';
import type { FieldsProgress, FileCoverage } from './layerSteps';
import { DraftSplit, draftDocType, emptyDraft } from './pageSplitPrefill';
import { SplitBand, bandsFromDrafts, splitColor } from './splitBands';
import type { SplitRequest } from './splitBeforeContext';
import {
    PageSpan,
    SplitPatch,
    applyFix,
    checkCoverage,
    deleteNeighbours,
    deleteSplit,
    docsMatchSplits,
    edgeGapFixes,
    fixOffer,
    formatSpan,
    mergeSpans,
    spansEqual,
    splitHere,
    splitHereBlocker,
    trimLeftOut,
    updateSplitByKey,
} from './splitRules';
import type { Coverage, DeleteTarget, FixOption } from './splitRules';
import { useSplitShortcuts } from './splitShortcuts';
import { splitsAsText } from './splitsAsText';
import { GtLeftOutPages, GtPageSplit, GtPageSplitInput } from './types';
import { useDocTypeOptions } from './useDocTypeOptions';
import { useFolderOptions } from './useFolderOptions';
import { answerSetVersion, useGridEditor } from './useGridEditor';
import type { GridSaveMutation } from './useGridEditor';
import { SeedSource, usePageSplitSeed } from './usePageSplitSeed';

const MAX_UNDO_STEPS = 100;

/** What the file page header shows about the editor. */
export interface EditorStatus {
    splitCount: number;
    verifiedCount: number;
    /** The documents the list shows, and how many of them are verified. */
    documentCount: number;
    documentsVerified: number;
    /** Fields progress of the listed documents that are saved as a document with a field group. */
    fields: FieldsProgress[];
    unsavedCount: number;
    /** What the drafts cover of the file; null until the page count is known. */
    coverage: FileCoverage | null;
}

interface HistoryEntry {
    label: string;
    drafts: DraftSplit[];
    leftOutDraft: PageSpan[] | null;
}

/**
 * Layer 1 editor: page-range splits for an ingested file.
 *
 * NOTE the splits endpoints are keyed by `ingestedFileId`, not the `$docId`
 * route param — the caller resolves it (from the overview row) and passes it in.
 */
const draftRange = (d: DraftSplit) =>
    rangeLabel(d.startPage || '?', d.endPage || '?');

const pageSpanText = (d: DraftSplit) =>
    d.startPage === d.endPage
        ? d.startPage || '?'
        : `${d.startPage || '?'}–${d.endPage || '?'}`;

export function PageSplitEditor({
    ingestedFileId,
    onShowPage,
    onBandsChange,
    onStatusChange,
    onOpenDocChange,
    currentPage,
    nextFile,
    previousFile,
    listName = null,
    splitRequest,
    thumbnailsView = false,
}: {
    ingestedFileId: number;
    /** Starts a split before a page, as "Split here" does; each new `seq` is one request. */
    splitRequest?: SplitRequest | null;
    /** The next file in the list the person came from; set only when there is one. */
    nextFile?: () => void;
    /** Opens the previous file in that list (P); set only when there is one. */
    previousFile?: () => void;
    /** Name of the list the person came from; the Previous and Next buttons show only when set. */
    listName?: string | null;
    /** Brings the preview to a 1-based file page; the row jump buttons show only when set. */
    onShowPage?: (page: number) => void;
    /** The 1-based file page now in view in the preview; "Split here" and "Use current page" need it. */
    currentPage?: number | null;
    /** Receives the current, unsaved rows as split bands (null until loaded and on unmount). */
    onBandsChange?: (bands: SplitBand[] | null) => void;
    /** Receives the counts the page header shows (null until loaded and on unmount). */
    onStatusChange?: (status: EditorStatus | null) => void;
    /** Receives the saved document the open split makes, or null when no split is open. */
    onOpenDocChange?: (docId: number | null) => void;
    /** Swaps the editable rows for a compact list that scrolls the thumbnail grid; the editor's state is kept. */
    thumbnailsView?: boolean;
}) {
    // Keyed so the frozen prefill, the saved/consumed flags and the touched
    // rows all reset when the route reuses this component for another file.
    return (
        <PageSplitEditorForFile
            key={ingestedFileId}
            ingestedFileId={ingestedFileId}
            onShowPage={onShowPage}
            onBandsChange={onBandsChange}
            onStatusChange={onStatusChange}
            onOpenDocChange={onOpenDocChange}
            currentPage={currentPage ?? null}
            nextFile={nextFile}
            previousFile={previousFile}
            listName={listName}
            splitRequest={splitRequest ?? null}
            thumbnailsView={thumbnailsView}
        />
    );
}

function PageSplitEditorForFile({
    ingestedFileId,
    onShowPage,
    onBandsChange,
    onStatusChange,
    onOpenDocChange,
    currentPage,
    nextFile,
    previousFile,
    listName,
    splitRequest,
    thumbnailsView,
}: {
    ingestedFileId: number;
    onShowPage?: (page: number) => void;
    onBandsChange?: (bands: SplitBand[] | null) => void;
    onStatusChange?: (status: EditorStatus | null) => void;
    onOpenDocChange?: (docId: number | null) => void;
    currentPage: number | null;
    nextFile?: () => void;
    previousFile?: () => void;
    listName: string | null;
    splitRequest: SplitRequest | null;
    thumbnailsView: boolean;
}) {
    const {
        data: existing,
        isLoading: splitsLoading,
        isFetching: splitsFetching,
        isError: splitsError,
        isRefetching: splitsRefetching,
        refetch: refetchSplits,
    } = useGtPageSplits(ingestedFileId);
    const { data: docTypes, isLoading: docTypesLoading } = useDocTypeOptions();
    const {
        data: folderOptions,
        isLoading: foldersLoading,
        isError: foldersError,
        isRefetching: foldersRefetching,
        refetch: refetchFolders,
        emptyMessage: folderEmptyMessage,
    } = useFolderOptions();
    const foldersFailed = foldersError && folderOptions.length === 0;
    const prefilledRef = useRef(false);
    const save = useSaveGtPageSplits(ingestedFileId, prefilledRef);

    // Same query key the file page reads for its doc switcher / single-doc
    // auto-forward (`GroundTruthFilePage`'s `useGtFileDocs`) — React Query
    // dedupes it, so this doesn't add a second request. Reused here (rather
    // than adding a new count source) to size the "Create documents" confirm
    // dialog around however many docs already exist on this file.
    const {
        data: existingDocs,
        isPending: docsPending,
        isError: docsError,
        failureCount: docsFailureCount,
        refetch: refetchExistingDocs,
    } = useGtFileDocs(ingestedFileId);
    const existingDocsCount = existingDocs?.length ?? 0;
    // The backend's replace guard, asked up front. Only an explicit `false`
    // blocks: while loading, or if this read fails, the button stays usable and
    // the server's 409 on the create call remains the backstop.
    const { data: createCheck, refetch: refetchCreateCheck } =
        useGtCreateDocsCheck(ingestedFileId);
    const createBlockedReason =
        createCheck?.canReplace === false
            ? (createCheck.reason ??
              "This file's documents cannot be replaced.")
            : null;
    const createDocs = useCreateGtDocsFromSplits(ingestedFileId);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [isCheckingExistingDocs, setIsCheckingExistingDocs] = useState(false);

    // Real PDF page count, used to bound the end-page input below. Fetched
    // eagerly (not gated behind a button) since it's a cheap, always-relevant
    // read. Graceful degradation: if the fetch fails or 4xxs (e.g. the source
    // isn't a PDF), `pageCount` stays undefined and the upper-bound check is
    // skipped — the other validations remain fully functional without it.
    const { data: pageCountData } = useGtPageCount(ingestedFileId);
    const maxPage = pageCountData?.pageCount ?? null;

    // Pages left out on purpose: the saved ones until the operator changes them.
    const { data: savedLeftOutRanges } = useGtLeftOutPages(ingestedFileId);
    const saveLeftOut = useSaveGtLeftOutPages(ingestedFileId);
    const savedLeftOut = useMemo(
        () => (savedLeftOutRanges ?? []).map(leftOutToSpan),
        [savedLeftOutRanges],
    );
    const [leftOutDraft, setLeftOutDraft] = useState<PageSpan[] | null>(null);
    const leftOut = leftOutDraft ?? savedLeftOut;
    // One entry per undoable change, holding the state from before it (the Verified ticks ride in the rows).
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    // The field a run of typing is in; it joins into one undo step until focus leaves the editor's fields.
    const typingRunRef = useRef<string | null>(null);
    const clearHistory = useCallback(() => {
        typingRunRef.current = null;
        setHistory([]);
    }, []);
    // What the save below writes: kept current each render so the save reads the latest.
    const keptLeftOutRef = useRef<PageSpan[]>([]);
    const savedLeftOutRef = useRef<PageSpan[]>([]);
    savedLeftOutRef.current = savedLeftOut;

    const validate = useCallback(
        (drafts: DraftSplit[]) => {
            const { errors, payload } = validateDrafts(
                drafts,
                maxPage,
                leftOut,
            );
            return {
                errors,
                payload: applySavedNotes(
                    applyUnsureMark(
                        payload,
                        unsurePageKeys(existing),
                        (split) => `${split.startPage}-${split.endPage}`,
                    ),
                    existing ?? [],
                ),
            };
        },
        [maxPage, existing, leftOut],
    );

    const { seedSource, seed, shouldPrefill, awaitingPrefill, prefilled } =
        usePageSplitSeed({
            existing,
            docs: existingDocs,
            docsPending,
            docsError,
            docsFailureCount,
            docTypes,
            docTypesLoading,
            folderOptions,
            foldersLoading,
            saveSucceeded: save.isSuccess,
        });
    prefilledRef.current = prefilled;

    // A network or 5xx failure keeps every edit; the banner's retry repeats only what is still unsaved.
    const [saveFailure, setSaveFailure] = useState<{
        message: string;
        stage: 'splits' | 'leftOut';
        options?: { confirmClear?: boolean };
    } | null>(null);
    const runLeftOutSave = (wanted: PageSpan[]) =>
        saveLeftOut.mutate(wanted.map(spanToLeftOut), {
            onSuccess: () => {
                setSaveFailure(null);
                setLeftOutDraft(null);
            },
            onError: (err) => {
                const cause = transientSaveFailure(err);
                if (cause === null) {
                    toast.error(getServerErrorMessage(err, 'left-out pages'));
                    return;
                }
                setSaveFailure({
                    message: `Couldn't save the left-out pages: ${cause}. Your documents are saved; the left-out pages are kept on this page. Press "Try saving again".`,
                    stage: 'leftOut',
                });
            },
        });
    const runLeftOutSaveRef = useRef(runLeftOutSave);
    runLeftOutSaveRef.current = runLeftOutSave;

    // Left-out pages are saved after the splits, so they never overlap the splits being replaced.
    const saveWithLeftOut = useMemo<GridSaveMutation<GtPageSplitInput[]>>(
        () => ({
            isPending: save.isPending || saveLeftOut.isPending,
            mutate: (request, options) => {
                setSaveFailure(null);
                save.mutate(request, {
                    onSuccess: (saved) => {
                        options?.onSuccess?.(saved);
                        clearHistory();
                        const wanted = keptLeftOutRef.current;
                        if (spansEqual(wanted, savedLeftOutRef.current)) {
                            setLeftOutDraft(null);
                            return;
                        }
                        runLeftOutSaveRef.current(wanted);
                    },
                    onError: (err) => {
                        const cause = transientSaveFailure(err);
                        if (cause === null) {
                            options?.onError?.(err);
                            return;
                        }
                        setSaveFailure({
                            message: `Couldn't save: ${cause}. Your edits are kept on this page. Press "Try saving again".`,
                            stage: 'splits',
                            options: { confirmClear: request.confirmClear },
                        });
                    },
                });
            },
        }),
        [save, saveLeftOut, clearHistory],
    );

    const {
        drafts,
        setDrafts,
        updateDraft,
        addDraft,
        isDirty: splitsDirty,
        hasEdits,
        clearCount,
        onSave,
        conflict,
        reloadSaved: reloadSavedSplits,
    } = useGridEditor<DraftSplit, GtPageSplitInput[], SeedSource | undefined>({
        savedCount: existing?.length ?? 0,
        savedVersion: existing && answerSetVersion(existing),
        reload: refetchSplits,
        existing: seedSource,
        seed,
        seedIsUnsaved: shouldPrefill,
        validate,
        save: saveWithLeftOut,
        successMessage: 'Page documents saved',
        entityType: 'page documents',
    });
    const draftsRef = useRef(drafts);
    draftsRef.current = drafts;
    const leftOutDraftRef = useRef(leftOutDraft);
    leftOutDraftRef.current = leftOutDraft;
    /** Remembers the state before a change; a `typingRun` key folds repeat edits to one field into one step. */
    const recordUndo = (label: string, typingRun?: string) => {
        if (typingRun !== undefined && typingRun === typingRunRef.current)
            return;
        typingRunRef.current = typingRun ?? null;
        const entry = {
            label,
            drafts: draftsRef.current,
            leftOutDraft: leftOutDraftRef.current,
        };
        setHistory((entries) => [
            ...entries.slice(-(MAX_UNDO_STEPS - 1)),
            entry,
        ]);
    };
    const splitRange = (key: string) => {
        const draft = draftsRef.current.find((d) => d.key === key);
        return draft ? draftRange(draft).toLowerCase() : 'this document';
    };
    const undo = () => {
        const last = history.at(-1);
        if (!last) return false;
        typingRunRef.current = null;
        setHistory(history.slice(0, -1));
        setDrafts(last.drafts);
        setLeftOutDraft(last.leftOutDraft);
        setDeleteKey(null);
        return true;
    };
    const reloadSaved = () => {
        clearHistory();
        setSaveFailure(null);
        setLeftOutDraft(null);
        reloadSavedSplits();
    };
    const isLoading = splitsLoading || awaitingPrefill;
    const bands = useMemo(
        () =>
            isLoading
                ? null
                : bandsFromDrafts(drafts, maxPage, (docType) =>
                      splitTypeLabel(docTypes ?? [], docType),
                  ),
        [isLoading, drafts, maxPage, docTypes],
    );
    useEffect(() => {
        onBandsChange?.(bands);
    }, [bands, onBandsChange]);
    useEffect(() => () => onBandsChange?.(null), [onBandsChange]);
    const unsurePages = useMemo(() => unsurePageKeys(existing), [existing]);
    const savedByRange = useMemo(
        () =>
            new Map(
                (existing ?? []).map((r) => [`${r.startPage}-${r.endPage}`, r]),
            ),
        [existing],
    );

    // Row messages stay quiet until the operator edits that row or tries to
    // save, so rows prefilled from the pipeline don't open covered in red.
    const [touchedKeys, setTouchedKeys] = useState<ReadonlySet<string>>(
        new Set(),
    );
    const [saveAttempted, setSaveAttempted] = useState(false);
    // One row is open for editing at a time.
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    // The row a failed save pointed at, ringed until it is edited; `n` re-fires the scroll.
    const [flagged, setFlagged] = useState<{ key: string; n: number } | null>(
        null,
    );
    const listRef = useRef<HTMLUListElement>(null);
    useEffect(() => {
        if (save.isSuccess) setSaveAttempted(false);
    }, [save.isSuccess]);
    // Every change to a split's start, end or type goes through `updateSplitByKey`, which clears its Verified tick.
    const editDraft = useCallback(
        (key: string, patch: SplitPatch) => {
            // Picking a type is one step each time; typing in Start or End folds into one.
            recordUndo(
                `edit ${splitRange(key)}`,
                'docType' in patch
                    ? undefined
                    : `${key}:${Object.keys(patch).join(',')}`,
            );
            setTouchedKeys((prev) => new Set(prev).add(key));
            setFlagged((current) => (current?.key === key ? null : current));
            setDrafts((rows) => updateSplitByKey(rows, key, patch));
        },
        [setDrafts],
    );
    const fixSplit = (option: FixOption) => {
        recordUndo(`fix for ${splitRange(option.key)}`);
        setTouchedKeys((prev) => new Set(prev).add(option.key));
        setDrafts((rows) => applyFix(rows, option));
    };

    const analysis = useMemo(
        () => analyzeDrafts(drafts, maxPage, leftOut),
        [drafts, maxPage, leftOut],
    );
    keptLeftOutRef.current = analysis.leftOut;
    const isDirty = splitsDirty || !spansEqual(analysis.leftOut, savedLeftOut);
    // Shows every problem and opens the first bad row.
    const revealProblems = () => {
        setSaveAttempted(true);
        const errorKeys = new Set(analysis.errorKeys.flat());
        const firstBad = drafts.find((d) => errorKeys.has(d.key));
        if (firstBad) {
            setExpandedKey(firstBad.key);
            setFlagged((current) => ({
                key: firstBad.key,
                n: (current?.n ?? 0) + 1,
            }));
        }
    };
    const handleSave = (options?: { confirmClear?: boolean }) => {
        revealProblems();
        onSave(options);
    };
    const retrySave = () => {
        if (saveFailure?.stage === 'leftOut') {
            setSaveFailure(null);
            runLeftOutSave(keptLeftOutRef.current);
            return;
        }
        handleSave(saveFailure?.options);
    };
    useEffect(() => {
        if (!flagged) return;
        listRef.current
            ?.querySelector(`[data-row-key="${flagged.key}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }, [flagged]);

    // A save reloads the rows under new keys; keep the open row open by finding it again.
    const expandedIndex = drafts.findIndex((d) => d.key === expandedKey);
    const lastExpandedRef = useRef<{ index: number; draft: DraftSplit } | null>(
        null,
    );
    if (expandedIndex >= 0) {
        lastExpandedRef.current = {
            index: expandedIndex,
            draft: drafts[expandedIndex],
        };
    }
    useLayoutEffect(() => {
        const last = lastExpandedRef.current;
        if (expandedKey === null || expandedIndex >= 0 || !last) return;
        const sameRow = drafts.find(
            (d) =>
                d.startPage === last.draft.startPage &&
                d.endPage === last.draft.endPage &&
                d.docType === last.draft.docType,
        );
        setExpandedKey((sameRow ?? drafts[last.index])?.key ?? null);
    }, [drafts, expandedKey, expandedIndex]);
    const { fieldErrors, coverage, pastEndRows } = analysis;
    // Rows past the last page show at once: a pre-fill can run off the end of the file.
    const pastEndKeys = new Set(pastEndRows.map((i) => drafts[i].key));
    const isShown = (key: string) =>
        saveAttempted || touchedKeys.has(key) || pastEndKeys.has(key);
    // A gap belongs to no row, so it shows once a save has been tried.
    const shownErrors = analysis.errors.filter((_, i) => {
        const keys = analysis.errorKeys[i];
        return keys.length === 0 ? saveAttempted : keys.some(isShown);
    });

    // A pre-fill alone is not an edit, so it does not warn on leave.
    useReportUnsavedChanges(
        hasEdits || !spansEqual(analysis.leftOut, savedLeftOut),
    );

    // The backend creates docs from the PERSISTED `existing` splits, not these
    // drafts — so the action is disabled while there are unsaved edits (save
    // first, then create) rather than silently saving on the operator's
    // behalf. Also requires at least one persisted split (the backend 422s
    // otherwise) and is unavailable while the splits/docs queries are still
    // loading, so the disabled/confirm state doesn't flicker on mount.
    const hasSavedSplits = (existing?.length ?? 0) > 0;
    const showCreateHint =
        save.isSuccess &&
        !isDirty &&
        hasSavedSplits &&
        existingDocs?.length === 0;
    const verifiedCount = drafts.filter((d) => d.checked).length;
    const verifyAllState: boolean | 'indeterminate' =
        verifiedCount === 0
            ? false
            : verifiedCount === drafts.length
              ? true
              : 'indeterminate';
    const verifyAll = (value: boolean) => {
        recordUndo(value ? 'verify all' : 'unverify all');
        setDrafts((rows) => rows.map((r) => ({ ...r, checked: value })));
    };

    // Row whose type picker should take focus once it has rendered open.
    const [focusTypeKey, setFocusTypeKey] = useState<string | null>(null);
    useEffect(() => {
        if (focusTypeKey === null) return;
        listRef.current
            ?.querySelector<HTMLElement>(
                `[data-testid="PageSplitEditor__docType-${focusTypeKey}"]`,
            )
            ?.focus();
        setFocusTypeKey(null);
    }, [focusTypeKey, expandedKey]);
    const addWholeFileSplit = () => {
        if (maxPage === null) return;
        const draft = {
            ...emptyDraft(),
            startPage: '1',
            endPage: String(maxPage),
        };
        recordUndo('add document');
        addDraft(() => draft);
        setExpandedKey(draft.key);
        setFocusTypeKey(draft.key);
    };
    const splitHereBlocked = splitHereBlocker(drafts, currentPage, maxPage);
    const splitAt = (page: number | null) => {
        const result = page === null ? null : splitHere(drafts, page, maxPage);
        if (!result) return;
        recordUndo('split here');
        setDrafts(result.rows);
        setExpandedKey(result.key);
        setFocusTypeKey(result.key);
    };
    const handleSplitHere = () => splitAt(currentPage);
    const handledRequestSeq = useRef(splitRequest?.seq);
    const splitAtRef = useRef(splitAt);
    splitAtRef.current = splitAt;
    useEffect(() => {
        if (!splitRequest || splitRequest.seq === handledRequestSeq.current)
            return;
        handledRequestSeq.current = splitRequest.seq;
        splitAtRef.current(splitRequest.page);
    }, [splitRequest]);

    // Deleting a split asks where its pages go; a row with no pages has nothing to place.
    const [deleteKey, setDeleteKey] = useState<string | null>(null);
    const deleteIndex = drafts.findIndex((d) => d.key === deleteKey);
    const deleteTo = deleteNeighbours(drafts, deleteIndex);
    const askDelete = (key: string) => {
        const row = drafts.find((d) => d.key === key);
        if (row && row.startPage === '' && row.endPage === '') {
            removeSplit(key, 'none');
        } else {
            setDeleteKey(key);
        }
    };
    const removeSplit = (key: string, target: DeleteTarget) => {
        const index = drafts.findIndex((d) => d.key === key);
        if (index < 0) return;
        recordUndo(`remove ${draftRange(drafts[index]).toLowerCase()}`);
        if (expandedKey === key) setExpandedKey(null);
        setDrafts((rows) =>
            deleteSplit(
                rows,
                rows.findIndex((d) => d.key === key),
                target,
            ),
        );
    };
    const leaveOut = (gap: PageSpan) => {
        recordUndo(`leaving out ${formatSpan(gap)}`);
        setLeftOutDraft(mergeSpans([...leftOut, gap]));
    };
    const putBack = (range: PageSpan) => {
        recordUndo(`putting back ${formatSpan(range)}`);
        setLeftOutDraft(
            leftOut.filter(
                (r) => r.start !== range.start || r.end !== range.end,
            ),
        );
    };
    const verifyOpenSplit = () => {
        const open = drafts.find((d) => d.key === expandedKey);
        if (!open) return;
        recordUndo(
            `${open.checked ? 'unverify' : 'verify'} ${splitRange(open.key)}`,
        );
        updateDraft(open.key, { checked: !open.checked });
    };
    const useCurrentPageAs = (field: 'startPage' | 'endPage') => {
        if (currentPage === null || expandedKey === null) return;
        editDraft(expandedKey, { [field]: String(currentPage) });
    };
    const stepPage = (by: number) => {
        if (!onShowPage || isLoading) return;
        const target = Math.max((currentPage ?? (by > 0 ? 0 : 2)) + by, 1);
        onShowPage(maxPage === null ? target : Math.min(target, maxPage));
    };
    const stepSplit = (by: number) => {
        if (drafts.length === 0) return;
        const current = drafts.findIndex((d) => d.key === expandedKey);
        const target =
            current < 0
                ? by > 0
                    ? 0
                    : drafts.length - 1
                : Math.min(Math.max(current + by, 0), drafts.length - 1);
        const row = drafts[target];
        setExpandedKey(row.key);
        if (onShowPage && isShowablePage(row.startPage, maxPage)) {
            onShowPage(Number(row.startPage));
        }
    };
    const primaryRef = useRef<HTMLButtonElement>(null);
    useSplitShortcuts({
        undo,
        previousSplit: () => stepSplit(-1),
        nextSplit: () => stepSplit(1),
        // Only the Save step is pressed: Create documents appears once nothing is unsaved.
        save: () => {
            if (isDirty) primaryRef.current?.click();
        },
        nextFile,
        previousFile,
        previousPage: () => stepPage(-1),
        nextPage: () => stepPage(1),
        splitHere: handleSplitHere,
        verify: verifyOpenSplit,
        useAsStart: () => useCurrentPageAs('startPage'),
        useAsEnd: () => useCurrentPageAs('endPage'),
    });
    const undoLabel = history.at(-1)?.label;

    const createDisabled =
        isLoading ||
        existingDocs === undefined ||
        isDirty ||
        !hasSavedSplits ||
        createBlockedReason !== null ||
        createDocs.isPending ||
        isCheckingExistingDocs;

    const runCreate = () => {
        createDocs.mutate(undefined, {
            onSuccess: (created) => {
                setCreateDialogOpen(false);
                toast.success(
                    `Created ${created.length} document${created.length === 1 ? '' : 's'}`,
                    QUIET_TOAST,
                );
            },
            onError: (err) => {
                toast.error(
                    getServerErrorMessage(err, 'documents from saved pages'),
                );
            },
        });
    };

    // `existingDocs` is a mount-time snapshot (no live refetch), so another
    // tab/operator could have created or removed docs since. Refetch at click
    // time so the confirm-vs-run-immediately branch below decides off fresh
    // data, not a possibly-stale closure value. This narrows but doesn't close
    // the race — the backend's own guard is the real source of truth.
    const handleCreateDocsClick = async () => {
        setIsCheckingExistingDocs(true);
        try {
            const [{ data: freshExistingDocs }, { data: freshCheck }] =
                await Promise.all([
                    refetchExistingDocs(),
                    refetchCreateCheck(),
                ]);
            if (freshCheck?.canReplace === false) {
                // Became blocked since load: the disabled state + reason now
                // show; don't offer a delete that cannot go ahead.
                return;
            }
            if ((freshExistingDocs?.length ?? 0) > 0) {
                setCreateDialogOpen(true);
            } else {
                runCreate();
            }
        } finally {
            setIsCheckingExistingDocs(false);
        }
    };

    // One primary button steps through the work: fix, save, create documents, then next file.
    const docsOutOfDate =
        existing !== undefined &&
        existingDocs !== undefined &&
        hasSavedSplits &&
        !docsMatchSplits(existing, existingDocs);
    const problemCount = analysis.errors.length;
    const noSplitsToSave = drafts.length === 0 && !hasSavedSplits && !prefilled;
    const unsavedCount = useMemo(() => {
        if (!isDirty) return 0;
        const saved = existing ?? [];
        let changed = 0;
        for (let i = 0; i < Math.max(drafts.length, saved.length); i++) {
            const d = drafts[i];
            const r = saved[i];
            const same =
                d !== undefined &&
                r !== undefined &&
                d.startPage === String(r.startPage + 1) &&
                d.endPage === String(r.endPage + 1) &&
                d.docType === draftDocType(r.docType) &&
                (d.folder ?? '') === (r.folder ?? '') &&
                d.remark.trim() === (r.remark ?? '') &&
                d.checked === r.checked;
            if (!same) changed += 1;
        }
        if (!spansEqual(analysis.leftOut, savedLeftOut)) changed += 1;
        return Math.max(changed, 1);
    }, [isDirty, existing, drafts, analysis.leftOut, savedLeftOut]);
    const fileCoverage = useMemo<FileCoverage | null>(
        () =>
            maxPage === null
                ? null
                : {
                      unit: 'pages',
                      total: maxPage,
                      missing: coverage.gaps.reduce(
                          (n, gap) => n + gap.end - gap.start + 1,
                          0,
                      ),
                      empty: drafts.length === 0,
                  },
        [maxPage, coverage.gaps, drafts.length],
    );

    // Documents sorted by first page, so a split with other pages than its document still finds the document it replaces.
    const sortedDocs = useMemo(
        () =>
            [...(existingDocs ?? [])].sort(
                (a, b) => (a.startPage ?? 0) - (b.startPage ?? 0),
            ),
        [existingDocs],
    );
    const exactDocFor = (row: DraftSplit) => {
        const start0 = Number(row.startPage) - 1;
        const end0 = Number(row.endPage) - 1;
        if (row.startPage === '' || row.endPage === '') return undefined;
        return existingDocs?.find(
            (d) => d.startPage === start0 && d.endPage === end0,
        );
    };
    const fieldsSig = drafts
        .flatMap((d) => {
            const doc = exactDocFor(d);
            return doc && doc.fieldsTotal > 0
                ? [`${doc.fieldsFilled}/${doc.fieldsTotal}`]
                : [];
        })
        .join(',');
    const fieldsProgress = useMemo<FieldsProgress[]>(
        () =>
            fieldsSig === ''
                ? []
                : fieldsSig.split(',').map((part) => {
                      const [filled, total] = part.split('/').map(Number);
                      return { filled, total };
                  }),
        [fieldsSig],
    );
    useEffect(() => {
        onStatusChange?.(
            isLoading
                ? null
                : {
                      splitCount: drafts.length,
                      verifiedCount,
                      documentCount: drafts.length,
                      documentsVerified: verifiedCount,
                      fields: fieldsProgress,
                      unsavedCount,
                      coverage: fileCoverage,
                  },
        );
    }, [
        isLoading,
        drafts.length,
        verifiedCount,
        fieldsProgress,
        unsavedCount,
        fileCoverage,
        onStatusChange,
    ]);
    useEffect(() => () => onStatusChange?.(null), [onStatusChange]);
    const { data: fileStatus } = useGtFileStatus(ingestedFileId);
    const uncoveredPages = coverage.gaps.reduce(
        (sum, gap) => sum + gap.end - gap.start + 1,
        0,
    );
    const hasFileRemark = (fileStatus?.fileRemark ?? '') !== '';
    const [section, setSection] = useState<'file' | 'documents'>(() =>
        window.location.hash === `#${FILE_SECTION_ID}` ? 'file' : 'documents',
    );
    useEffect(() => {
        const onOpenSection = (event: Event) => {
            const id = (event as CustomEvent<string>).detail;
            if (id === FILE_SECTION_ID) setSection('file');
            if (id === DOCUMENTS_SECTION_ID) setSection('documents');
        };
        window.addEventListener(OPEN_SECTION_EVENT, onOpenSection);
        return () =>
            window.removeEventListener(OPEN_SECTION_EVENT, onOpenSection);
    }, []);
    // Opening a row from anywhere (save blocked, the PDF toolbar) shows the Documents section.
    useEffect(() => {
        if (expandedKey !== null) setSection('documents');
    }, [expandedKey]);
    const staleSplitNumbers =
        existingDocsCount > 0
            ? drafts.flatMap((d, i) =>
                  (d.startPage === '' && d.endPage === '') || exactDocFor(d)
                      ? []
                      : [i + 1],
              )
            : [];
    const openDraft = expandedIndex >= 0 ? drafts[expandedIndex] : undefined;
    const openDocId = openDraft ? (exactDocFor(openDraft)?.id ?? null) : null;
    useEffect(() => {
        onOpenDocChange?.(expandedIndex >= 0 ? openDocId : null);
    }, [expandedIndex, openDocId, onOpenDocChange]);

    // Splits a person has opened, by pages, so the footer can say which still have not been looked at.
    const [openedSigs, setOpenedSigs] = useState<ReadonlySet<string>>(
        new Set(),
    );
    const sigOf = (d: DraftSplit) => `${d.startPage}-${d.endPage}`;
    const expandedSig =
        expandedIndex >= 0 ? sigOf(drafts[expandedIndex]) : null;
    useEffect(() => {
        if (expandedSig === null) return;
        setOpenedSigs((prev) =>
            prev.has(expandedSig) ? prev : new Set(prev).add(expandedSig),
        );
    }, [expandedSig]);
    const unopened =
        verifiedCount === drafts.length
            ? []
            : drafts.flatMap((d) =>
                  d.checked || openedSigs.has(sigOf(d))
                      ? []
                      : [pageSpanText(d)],
              );

    let step: ComponentProps<typeof EditorFooter>['step'] = null;
    if (problemCount > 0) {
        step = {
            label: `Fix ${problemCount} problem${problemCount === 1 ? '' : 's'} to save`,
            onClick: revealProblems,
            testId: 'PageSplitEditor__saveButton',
            className: 'bg-red-100 text-red-800 hover:bg-red-200',
        };
    } else if (saveFailure) {
        step = {
            label: 'Try saving again  (⌘S)',
            onClick: retrySave,
            disabled: save.isPending || saveLeftOut.isPending,
            testId: 'PageSplitEditor__retrySave',
        };
    } else if (!isDirty && !noSplitsToSave) {
        if (docsOutOfDate) {
            step = {
                label:
                    existingDocsCount > 0
                        ? `Create documents (replaces ${existingDocsCount})`
                        : 'Create documents',
                onClick: handleCreateDocsClick,
                disabled: createDisabled,
                title:
                    existingDocsCount > 0
                        ? "Replaces this file's documents with one per saved document. Can't be undone."
                        : undefined,
                testId: 'PageSplitEditor__createDocsButton',
            };
        }
    }
    const saveLabel = noSplitsToSave
        ? 'Add a document to save'
        : isDirty
          ? `Save ${unsavedCount} change${unsavedCount === 1 ? '' : 's'}  (⌘S)`
          : 'Save documents (⌘S)';
    const colorOfRow = (row: DraftSplit, index: number) => {
        const band = bands?.find(
            (b) =>
                String(b.first) === row.startPage.trim() &&
                String(b.last) === row.endPage.trim(),
        );
        return splitColor(band?.number ?? index + 1);
    };
    const savedOf = (row: DraftSplit) =>
        savedByRange.get(
            `${Number(row.startPage) - 1}-${Number(row.endPage) - 1}`,
        );
    const rowOrigin = (row: DraftSplit) => savedOf(row)?.draftedBy ?? null;
    const blockedRows = new Set(analysis.errorKeys.flat()).size;
    const blockedLabel = [
        blockedRows > 0
            ? `${blockedRows} ${blockedRows === 1 ? 'document has a problem' : 'documents have problems'} (the first one is open below)`
            : '',
        coverage.gaps.length > 0
            ? `${coverage.gaps.length} gap${coverage.gaps.length === 1 ? '' : 's'}: put the pages in a document or leave them out`
            : '',
    ]
        .filter(Boolean)
        .join('; ');

    const spanText = (span: PageSpan) =>
        rangeLabel(span.start, span.end).toLowerCase();
    const copyAsText = () =>
        splitsAsText({
            fileName: fileDisplayName(fileStatus),
            sizeText: maxPage === null ? null : pluralize(maxPage, 'page'),
            url: `${window.location.origin}/ground-truth/${ingestedFileId}`,
            rows: drafts.map((d) => ({
                name: draftRange(d),
                type: d.docType
                    ? splitTypeLabel(docTypes ?? [], d.docType)
                    : null,
                verified: d.checked,
                note: d.remark,
            })),
            leftOutText: analysis.leftOut.map(spanText).join(', '),
            gapsText: coverage.gaps.map(spanText).join(', '),
            fileNote: fileStatus?.fileRemark ?? '',
            verifiedCount,
            unsavedCount,
        });

    const addFirstSplit = () => {
        const draft = { ...emptyDraft(), startPage: '1' };
        recordUndo('add document');
        addDraft(() => draft);
        setExpandedKey(draft.key);
        setFocusTypeKey(draft.key);
    };

    return (
        <div
            className="flex h-full min-h-0 flex-col"
            data-testid="PageSplitEditor"
            onBlur={() => {
                typingRunRef.current = null;
            }}
        >
            <Accordion
                type="single"
                collapsible
                value={section}
                onValueChange={(value) =>
                    // Pressing the open header opens the other one, so one is always open.
                    setSection(
                        value === 'file' || value === 'documents'
                            ? value
                            : section === 'file'
                              ? 'documents'
                              : 'file',
                    )
                }
                className="flex min-h-0 flex-1 flex-col"
                data-testid="PageSplitEditor__sections"
            >
                <AccordionItem
                    value="file"
                    id={FILE_SECTION_ID}
                    className={sectionItemCls}
                >
                    <AccordionTrigger
                        className={sectionTriggerCls}
                        data-testid="PageSplitEditor__fileToggle"
                    >
                        <LayerChip step="file" />
                        File
                        <span
                            className="flex min-w-0 flex-1 items-center gap-1 truncate text-xs font-normal text-slate-600"
                            data-testid="PageSplitEditor__fileSummary"
                        >
                            {maxPage !== null && (
                                <>
                                    <span>{pluralize(maxPage, 'page')}</span>
                                    {!isLoading && (
                                        <>
                                            <span aria-hidden="true">·</span>
                                            {uncoveredPages > 0 ? (
                                                <span className="text-amber-700">
                                                    {uncoveredPages} not in a
                                                    document
                                                </span>
                                            ) : (
                                                <span>all in a document</span>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                            {hasFileRemark && (
                                <>
                                    {maxPage !== null && (
                                        <span aria-hidden="true">·</span>
                                    )}
                                    <MessageSquare
                                        aria-hidden="true"
                                        className="h-3 w-3 shrink-0"
                                    />
                                    <span>1 remark</span>
                                </>
                            )}
                        </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-3 pt-3">
                        <FileRemarkBox
                            ingestedFileId={ingestedFileId}
                            compact
                        />
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem
                    value="documents"
                    id={DOCUMENTS_SECTION_ID}
                    className={sectionItemCls}
                >
                    <AccordionTrigger
                        className={sectionTriggerCls}
                        data-testid="PageSplitEditor__documentsToggle"
                    >
                        <LayerChip step="documents" />
                        Documents
                        {!isLoading && (
                            <>
                                <span
                                    className="rounded-full bg-[#EFF9F9] px-1.5 text-xs font-normal text-[#476969]"
                                    data-testid="PageSplitEditor__documentCount"
                                >
                                    {drafts.length}
                                </span>
                                <span
                                    className="min-w-0 flex-1 truncate text-xs font-normal text-slate-600"
                                    data-testid="PageSplitEditor__documentsSummary"
                                >
                                    {verifiedCount} of {drafts.length} verified
                                </span>
                            </>
                        )}
                    </AccordionTrigger>
                    <AccordionContent
                        forceMount
                        className="flex h-full flex-col gap-2 overflow-auto px-4 py-3"
                    >
                        {!isLoading && drafts.length === 0 && (
                            <p className="text-[13px] text-slate-600">
                                {existingDocsCount > 0
                                    ? `No documents listed yet. This file has ${existingDocsCount} saved ${pluralize(existingDocsCount, 'document')} from before.`
                                    : 'No documents yet'}
                            </p>
                        )}
                        {!isLoading && (
                            <CoveragePanel
                                pageCount={maxPage}
                                coverage={coverage}
                                leftOut={analysis.leftOut}
                                hasSplits={drafts.length > 0}
                                edgeFixes={(gap) => edgeGapFixes(drafts, gap)}
                                onFix={fixSplit}
                                onLeaveOut={leaveOut}
                                onPutBack={putBack}
                                onShowPage={onShowPage}
                            />
                        )}
                        {!isLoading && saveAttempted && problemCount > 0 && (
                            <div
                                role="alert"
                                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] leading-[18px] text-red-800"
                                data-testid="PageSplitEditor__blockedAlert"
                            >
                                Not saved. {blockedLabel}.
                            </div>
                        )}
                        {saveFailure && (
                            <div
                                role="alert"
                                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] leading-[18px] text-red-800"
                                data-testid="PageSplitEditor__saveFailure"
                            >
                                {saveFailure.message}
                            </div>
                        )}
                        <ClaudeUnsureNote saved={existing} />
                        {!isLoading &&
                            pastEndRows.length > 0 &&
                            maxPage != null && (
                                <p
                                    className="flex items-start gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                                    data-testid="PageSplitEditor__pastEndWarning"
                                >
                                    <TriangleAlert className="h-3.5 w-3.5 mt-px shrink-0" />
                                    {pastEndRows
                                        .map((i) => draftRange(drafts[i]))
                                        .join(', ')}{' '}
                                    {pastEndRows.length === 1 &&
                                    draftRange(
                                        drafts[pastEndRows[0]],
                                    ).startsWith('Page ')
                                        ? 'goes'
                                        : 'go'}{' '}
                                    past the last page of this file (page{' '}
                                    {maxPage}). Fix{' '}
                                    {pastEndRows.length === 1 ? 'it' : 'them'}{' '}
                                    before saving.
                                </p>
                            )}
                        {foldersFailed && (
                            <LoadError
                                what="the binder folders"
                                onRetry={() => void refetchFolders()}
                                isRetrying={foldersRefetching}
                                data-testid="PageSplitEditor__foldersError"
                            />
                        )}
                        {existing === undefined && splitsError && (
                            <LoadError
                                what="the saved page documents"
                                onRetry={() => void refetchSplits()}
                                isRetrying={splitsRefetching}
                                data-testid="PageSplitEditor__error"
                            />
                        )}
                        {isLoading ? (
                            <PanelSkeleton
                                rows={3}
                                data-testid="PageSplitEditor__loading"
                            />
                        ) : (
                            <>
                                <div
                                    className="flex items-center justify-between gap-2"
                                    data-testid="PageSplitEditor__listHeader"
                                >
                                    <CopyAsTextButton
                                        getText={copyAsText}
                                        testId="PageSplitEditor__copyMarkdown"
                                    />
                                    <Hint
                                        text={
                                            undoLabel === undefined
                                                ? 'Nothing to undo since your last save.'
                                                : 'Undoes edits since your last save.'
                                        }
                                        focusable={undoLabel === undefined}
                                    >
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-8 text-xs"
                                            disabled={undoLabel === undefined}
                                            onClick={undo}
                                            data-testid="PageSplitEditor__undoButton"
                                        >
                                            <Undo2 className="mr-1 h-3.5 w-3.5" />
                                            {undoLabel === undefined
                                                ? 'Undo'
                                                : `Undo ${undoLabel}`}
                                            <Kbd className="ml-2">⌘Z</Kbd>
                                        </Button>
                                    </Hint>
                                </div>
                                {thumbnailsView ? (
                                    <SplitJumpList
                                        drafts={drafts}
                                        docTypes={docTypes ?? []}
                                        colorOf={colorOfRow}
                                        originOf={rowOrigin}
                                        onShowPage={onShowPage}
                                    />
                                ) : (
                                    <>
                                        {drafts.length === 0 && (
                                            <div
                                                className="flex flex-col gap-3 rounded-xl border border-dashed border-[#CBE1E1] p-4"
                                                data-testid="PageSplitEditor__emptyState"
                                            >
                                                <p className="text-sm">
                                                    Neither Claude nor a person
                                                    has split this file into
                                                    documents yet. Pick how to
                                                    start.
                                                </p>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="self-start"
                                                    disabled={maxPage === null}
                                                    onClick={addWholeFileSplit}
                                                    data-testid="PageSplitEditor__wholeFileButton"
                                                >
                                                    Whole file is one document
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="self-start"
                                                    onClick={addFirstSplit}
                                                    data-testid="PageSplitEditor__addFirstSplit"
                                                >
                                                    + Add first document at page
                                                    1
                                                </Button>
                                                <p className="text-xs text-slate-600">
                                                    Scroll the PDF and use
                                                    &quot;Split here&quot; where
                                                    the next document starts.
                                                </p>
                                                <p className="text-[13px] leading-5 text-slate-600">
                                                    <strong className="font-semibold text-foreground">
                                                        After saving:
                                                    </strong>{' '}
                                                    press{' '}
                                                    <em>Create documents</em> to
                                                    turn each saved document
                                                    into a searchable one you
                                                    can search and score.
                                                </p>
                                            </div>
                                        )}
                                        <ul
                                            ref={listRef}
                                            className="flex flex-col gap-2"
                                            data-testid="PageSplitEditor__list"
                                        >
                                            {drafts.map((row, index) => {
                                                const start0 =
                                                    Number(row.startPage) - 1;
                                                const end0 =
                                                    Number(row.endPage) - 1;
                                                const exactDoc =
                                                    exactDocFor(row);
                                                const shownDoc =
                                                    exactDoc ??
                                                    sortedDocs[index];
                                                return (
                                                    <PageSplitRow
                                                        key={row.key}
                                                        row={row}
                                                        index={index}
                                                        isExpanded={
                                                            expandedKey ===
                                                            row.key
                                                        }
                                                        onToggle={() => {
                                                            const opening =
                                                                expandedKey !==
                                                                row.key;
                                                            setExpandedKey(
                                                                opening
                                                                    ? row.key
                                                                    : null,
                                                            );
                                                            if (
                                                                opening &&
                                                                onShowPage &&
                                                                isShowablePage(
                                                                    row.startPage,
                                                                    maxPage,
                                                                )
                                                            ) {
                                                                onShowPage(
                                                                    Number(
                                                                        row.startPage,
                                                                    ),
                                                                );
                                                            }
                                                        }}
                                                        isFlagged={
                                                            flagged?.key ===
                                                            row.key
                                                        }
                                                        draftedBy={rowOrigin(
                                                            row,
                                                        )}
                                                        editedBy={
                                                            savedOf(row)
                                                                ?.editedBy ??
                                                            null
                                                        }
                                                        checkedBy={
                                                            savedOf(row)
                                                                ?.checkedBy ??
                                                            null
                                                        }
                                                        color={colorOfRow(
                                                            row,
                                                            index,
                                                        )}
                                                        docStale={
                                                            exactDoc ===
                                                                undefined &&
                                                            shownDoc !==
                                                                undefined &&
                                                            staleSplitNumbers.includes(
                                                                index + 1,
                                                            )
                                                        }
                                                        deleteChoice={
                                                            deleteKey ===
                                                            row.key
                                                                ? {
                                                                      previous:
                                                                          deleteTo.previous,
                                                                      next: deleteTo.next,
                                                                      previousLabel:
                                                                          deleteTo.previous ===
                                                                          null
                                                                              ? ''
                                                                              : draftRange(
                                                                                    drafts[
                                                                                        deleteTo
                                                                                            .previous
                                                                                    ],
                                                                                ),
                                                                      nextLabel:
                                                                          deleteTo.next ===
                                                                          null
                                                                              ? ''
                                                                              : draftRange(
                                                                                    drafts[
                                                                                        deleteTo
                                                                                            .next
                                                                                    ],
                                                                                ),
                                                                      onChoose:
                                                                          (
                                                                              target,
                                                                          ) =>
                                                                              removeSplit(
                                                                                  row.key,
                                                                                  target,
                                                                              ),
                                                                      onCancel:
                                                                          () =>
                                                                              setDeleteKey(
                                                                                  null,
                                                                              ),
                                                                  }
                                                                : null
                                                        }
                                                        rowErrors={
                                                            isShown(row.key)
                                                                ? (fieldErrors[
                                                                      row.key
                                                                  ] ?? {})
                                                                : {}
                                                        }
                                                        claudeUnsure={unsurePages.has(
                                                            `${start0}-${end0}`,
                                                        )}
                                                        docTypes={
                                                            docTypes ?? []
                                                        }
                                                        folderOptions={
                                                            folderOptions
                                                        }
                                                        foldersLoading={
                                                            foldersLoading
                                                        }
                                                        folderEmptyMessage={
                                                            folderEmptyMessage
                                                        }
                                                        maxPage={maxPage}
                                                        ingestedFileId={
                                                            ingestedFileId
                                                        }
                                                        matchedDocId={
                                                            shownDoc?.id ?? null
                                                        }
                                                        fieldsFilled={
                                                            shownDoc?.fieldsFilled
                                                        }
                                                        fieldsTotal={
                                                            shownDoc?.fieldsTotal
                                                        }
                                                        onEdit={(patch) =>
                                                            editDraft(
                                                                row.key,
                                                                patch,
                                                            )
                                                        }
                                                        currentPage={
                                                            currentPage
                                                        }
                                                        fixOffer={fixOffer(
                                                            drafts,
                                                            index,
                                                            maxPage,
                                                            analysis.leftOut,
                                                        )}
                                                        onFix={fixSplit}
                                                        onCheckedChange={(
                                                            value,
                                                        ) => {
                                                            recordUndo(
                                                                `${value ? 'verify' : 'unverify'} ${draftRange(row).toLowerCase()}`,
                                                            );
                                                            updateDraft(
                                                                row.key,
                                                                {
                                                                    checked:
                                                                        value,
                                                                },
                                                            );
                                                        }}
                                                        onRemove={() =>
                                                            askDelete(row.key)
                                                        }
                                                        onShowPage={onShowPage}
                                                    />
                                                );
                                            })}
                                        </ul>
                                        {drafts.some(
                                            (row, index) =>
                                                (exactDocFor(row) ??
                                                    sortedDocs[index]) ===
                                                undefined,
                                        ) && (
                                            <p
                                                className="flex items-center gap-1 text-xs text-[#476969]"
                                                data-testid="PageSplitEditor__fieldsHint"
                                            >
                                                <Info
                                                    aria-hidden="true"
                                                    className="h-3.5 w-3.5 shrink-0"
                                                />
                                                Fields open after you press
                                                Create documents.
                                            </p>
                                        )}
                                        {drafts.length > 0 && (
                                            <div className="grid grid-cols-2 gap-2 pt-1">
                                                <Hint
                                                    className="flex"
                                                    text={
                                                        splitHereBlocked ??
                                                        'Start a new document at the page now in the preview.'
                                                    }
                                                    focusable={
                                                        isLoading ||
                                                        splitHereBlocked !==
                                                            null
                                                    }
                                                >
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="w-full"
                                                        disabled={
                                                            isLoading ||
                                                            splitHereBlocked !==
                                                                null
                                                        }
                                                        onClick={
                                                            handleSplitHere
                                                        }
                                                        data-testid="PageSplitEditor__splitHereButton"
                                                    >
                                                        <Scissors className="mr-1 h-3.5 w-3.5" />
                                                        {currentPage === null
                                                            ? 'Split here'
                                                            : `Split at page ${currentPage}`}
                                                        <Kbd className="ml-2 text-slate-600">
                                                            S
                                                        </Kbd>
                                                    </Button>
                                                </Hint>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full"
                                                    disabled={isLoading}
                                                    onClick={() => {
                                                        const draft =
                                                            emptyDraft();
                                                        recordUndo(
                                                            'add document',
                                                        );
                                                        addDraft(() => draft);
                                                        setExpandedKey(
                                                            draft.key,
                                                        );
                                                    }}
                                                >
                                                    <Plus className="mr-1 h-3.5 w-3.5" />{' '}
                                                    Add document
                                                </Button>
                                                {currentPage !== null &&
                                                    splitHereBlocked ===
                                                        null && (
                                                        <span className="col-span-2 text-xs text-slate-600">
                                                            Ends the document at{' '}
                                                            {currentPage} and
                                                            starts a new one at{' '}
                                                            {currentPage + 1}.
                                                        </span>
                                                    )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        <EditorFooter
                            inline
                            leading={
                                <div className="flex min-w-0 flex-col gap-2.5 text-sm">
                                    <div className="flex items-center justify-between gap-2">
                                        <Hint
                                            text="Tick every document as Verified."
                                            focusable={
                                                save.isPending ||
                                                splitsFetching ||
                                                drafts.length === 0
                                            }
                                        >
                                            <label className="flex items-center gap-2 whitespace-nowrap font-medium">
                                                <Checkbox
                                                    checked={verifyAllState}
                                                    onCheckedChange={(value) =>
                                                        verifyAll(
                                                            value === true,
                                                        )
                                                    }
                                                    disabled={
                                                        save.isPending ||
                                                        splitsFetching ||
                                                        drafts.length === 0
                                                    }
                                                    aria-label="Verify all documents"
                                                    data-testid="PageSplitEditor__checkedMark"
                                                />
                                                Verify all
                                            </label>
                                        </Hint>
                                        <span
                                            className="whitespace-nowrap text-[13px] text-slate-600"
                                            data-testid="PageSplitEditor__verifiedCount"
                                        >
                                            {verifiedCount} of {drafts.length}{' '}
                                            verified
                                        </span>
                                    </div>
                                    <div
                                        role="progressbar"
                                        aria-label="Documents verified"
                                        aria-valuemin={0}
                                        aria-valuenow={verifiedCount}
                                        aria-valuemax={drafts.length}
                                        className="h-1 w-full overflow-hidden rounded-full bg-[#E2F0F0]"
                                        data-testid="PageSplitEditor__verifiedProgress"
                                    >
                                        <div
                                            className="h-full bg-[#32ACAC]"
                                            style={{
                                                width: `${drafts.length === 0 ? 0 : Math.round((100 * verifiedCount) / drafts.length)}%`,
                                            }}
                                        />
                                    </div>
                                    {unopened.length > 0 && (
                                        <span
                                            className="flex items-center gap-1 text-xs text-amber-700"
                                            data-testid="PageSplitEditor__unopened"
                                        >
                                            <EyeOff
                                                aria-hidden="true"
                                                className="h-3.5 w-3.5 shrink-0"
                                            />
                                            Not opened yet: pages{' '}
                                            {unopened.join(', ')}
                                        </span>
                                    )}
                                </div>
                            }
                            errors={analysis.errors}
                            hideErrorList
                            onSave={handleSave}
                            isPending={save.isPending}
                            disabled={
                                existing === undefined ||
                                isLoading ||
                                noSplitsToSave
                            }
                            loadError={
                                existing === undefined && splitsError
                                    ? loadErrorMessage('page documents')
                                    : null
                            }
                            clearCount={clearCount}
                            clearNoun="page document"
                            conflict={conflict}
                            onReload={reloadSaved}
                            saveLabel={saveLabel}
                            step={step}
                            primaryRef={primaryRef}
                            testId="PageSplitEditor__saveButton"
                        />

                        <AlertDialog
                            open={createDialogOpen}
                            onOpenChange={setCreateDialogOpen}
                        >
                            <AlertDialogContent data-testid="PageSplitEditor__createDocsDialog">
                                <AlertDialogHeader>
                                    <AlertDialogTitle>
                                        Replace this file&apos;s documents?
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This will delete the current{' '}
                                        {existingDocsCount} document
                                        {existingDocsCount === 1 ? '' : 's'} and
                                        their annotations and recreate them from
                                        the saved page documents. This cannot be
                                        undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel
                                        disabled={createDocs.isPending}
                                        data-testid="PageSplitEditor__createDocsCancelButton"
                                    >
                                        Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={runCreate}
                                        disabled={createDocs.isPending}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        data-testid="PageSplitEditor__createDocsConfirmButton"
                                    >
                                        {createDocs.isPending
                                            ? 'Creating…'
                                            : 'Delete and recreate'}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                        {showCreateHint && (
                            <p
                                className="text-xs text-slate-600"
                                data-testid="PageSplitEditor__createHint"
                            >
                                Saved. Use Create documents to make these
                                documents searchable.
                            </p>
                        )}
                        {createBlockedReason !== null && (
                            <p
                                className="text-xs text-slate-600"
                                data-testid="PageSplitEditor__createDocsBlockedReason"
                            >
                                {createBlockedReason}
                            </p>
                        )}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            {listName && (
                <FileNavBar
                    listName={listName}
                    onPrevious={previousFile}
                    onNext={nextFile}
                />
            )}
        </div>
    );
}

const sectionItemCls =
    'flex min-h-0 flex-col border-b border-[#E2F0F0] data-[state=open]:flex-1 [&>[role=region]]:min-h-0 [&>[role=region]]:flex-1';
const sectionTriggerCls =
    'gap-2.5 px-4 py-3 text-left text-[15px] font-semibold hover:no-underline';

function Kbd({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) {
    return (
        <kbd
            className={cn(
                'rounded border border-b-2 border-[#CBE1E1] bg-white px-1.5 font-mono text-xs',
                className,
            )}
        >
            {children}
        </kbd>
    );
}

/** The thumbnail view's list of splits: each one scrolls the grid to its first page. */
function SplitJumpList({
    drafts,
    docTypes,
    colorOf,
    originOf,
    onShowPage,
}: {
    drafts: DraftSplit[];
    docTypes: ComponentProps<typeof PageSplitRow>['docTypes'];
    colorOf: (row: DraftSplit, index: number) => string;
    originOf: (row: DraftSplit) => string | null;
    onShowPage?: (page: number) => void;
}) {
    return (
        <>
            <ul
                className="flex flex-col gap-2"
                data-testid="PageSplitEditor__jumpList"
            >
                {drafts.map((row, index) => {
                    const origin = originOf(row);
                    return (
                        <li key={row.key}>
                            <button
                                type="button"
                                title={`Scroll the grid to page ${row.startPage}`}
                                disabled={
                                    !onShowPage ||
                                    !isShowablePage(row.startPage, null)
                                }
                                onClick={() =>
                                    onShowPage?.(Number(row.startPage))
                                }
                                className="flex w-full items-center gap-3 rounded-xl border border-l-[5px] border-[#E2F0F0] bg-white px-3 py-2 text-left text-sm"
                                style={{
                                    borderLeftColor: colorOf(row, index),
                                }}
                            >
                                <span className="font-medium tabular-nums">
                                    {draftRange(row)}
                                </span>
                                <span className="min-w-0 flex-1 truncate text-[13px] text-slate-600">
                                    {splitTypeLabel(docTypes, row.docType)}
                                </span>
                                <span className="text-xs text-slate-600">
                                    {row.checked
                                        ? 'Verified'
                                        : origin === null
                                          ? 'Person'
                                          : origin === 'pipeline'
                                            ? 'Pipeline'
                                            : 'Claude'}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
            <p className="text-xs leading-[18px] text-slate-600">
                Clicking a document scrolls the grid to its first page. Open the
                document in Pages view to edit its type and folder.
            </p>
        </>
    );
}

const leftOutToSpan = (r: GtLeftOutPages): PageSpan => ({
    start: r.startPage + 1,
    end: r.endPage + 1,
});
const spanToLeftOut = (s: PageSpan): GtLeftOutPages => ({
    startPage: s.start - 1,
    endPage: s.end - 1,
});

/** Where every page of the file stands: in one split, in none (a gap that blocks saving), in two, or left out. */
function CoveragePanel({
    pageCount,
    coverage,
    leftOut,
    hasSplits,
    edgeFixes,
    onFix,
    onLeaveOut,
    onPutBack,
    onShowPage,
}: {
    pageCount: number | null;
    coverage: Coverage;
    leftOut: PageSpan[];
    hasSplits: boolean;
    /** Ways to give a gap before the first split or after the last one to the split beside it. */
    edgeFixes: (gap: PageSpan) => FixOption[];
    onFix: (option: FixOption) => void;
    onLeaveOut: (gap: PageSpan) => void;
    onPutBack: (range: PageSpan) => void;
    onShowPage?: (page: number) => void;
}) {
    const { gaps, overlaps } = coverage;
    const ok = gaps.length === 0 && overlaps.length === 0;
    // Nothing to fix and no left-out pages to put back: a green tick would only take room.
    if (pageCount === null || ((ok || !hasSplits) && leftOut.length === 0))
        return null;
    const count = (span: PageSpan) => span.end - span.start + 1;
    const pages = (span: PageSpan) =>
        `${count(span)} ${count(span) === 1 ? 'page' : 'pages'}`;
    const jump = (span: PageSpan) =>
        onShowPage && (
            <button
                type="button"
                className="font-medium underline"
                onClick={() => onShowPage(span.start)}
            >
                Jump to page {span.start}
            </button>
        );
    const actionCls = 'h-8 border-current bg-white/60 text-xs';
    return (
        <section
            className={cn(
                'flex flex-col gap-1 rounded-lg border px-3 py-2 text-[13px] leading-[18px]',
                ok
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-red-200 bg-red-50 text-red-800',
            )}
            data-testid="PageSplitEditor__coverage"
        >
            <span className="font-semibold">
                {ok
                    ? `✓ All ${pageCount} pages are in exactly one document${leftOut.length > 0 ? ' or left out' : ''}`
                    : `${coverage.coveredCount} of ${pageCount} pages in a document`}
            </span>
            {gaps.map((gap) => (
                <span
                    key={`gap-${gap.start}`}
                    className="flex flex-wrap items-center gap-2"
                    data-testid="PageSplitEditor__gap"
                >
                    {pages(gap)} in no document ({formatSpan(gap)}). {jump(gap)}
                    {edgeFixes(gap).map((option) => (
                        <Button
                            key={option.label}
                            type="button"
                            variant="outline"
                            size="sm"
                            className={actionCls}
                            onClick={() => onFix(option)}
                        >
                            {option.label}
                        </Button>
                    ))}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={actionCls}
                        onClick={() => onLeaveOut(gap)}
                    >
                        Leave {formatSpan(gap)} out (blank or cover)
                    </Button>
                </span>
            ))}
            {overlaps.map((overlap) => (
                <span
                    key={`overlap-${overlap.start}`}
                    className="flex flex-wrap items-center gap-2"
                    data-testid="PageSplitEditor__overlap"
                >
                    {pages(overlap)} in two documents ({formatSpan(overlap)}).{' '}
                    {jump(overlap)}
                </span>
            ))}
            {leftOut.length > 0 && (
                <span
                    className="flex flex-wrap items-center gap-2"
                    data-testid="PageSplitEditor__leftOut"
                >
                    <span className="flex items-center gap-1">
                        Left out on purpose:
                        <HoverNote text="Pages that belong in no document, like a blank or cover page. They don't count as missing." />
                    </span>
                    {leftOut.map((range) => (
                        <span
                            key={`out-${range.start}`}
                            className="flex items-center gap-1"
                        >
                            {formatSpan(range)}
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={actionCls}
                                onClick={() => onPutBack(range)}
                            >
                                Put {formatSpan(range)} back
                            </Button>
                        </span>
                    ))}
                </span>
            )}
        </section>
    );
}

type SplitField = 'startPage' | 'endPage' | 'docType';

export interface SplitAnalysis {
    /** Every blocking message, row-prefixed, for the footer and the save gate. */
    errors: string[];
    /** Parallel to `errors`: the row keys each message is about. */
    errorKeys: string[][];
    /** Per-row, per-field messages to show next to the field as typed. */
    fieldErrors: Record<string, Partial<Record<SplitField, string[]>>>;
    /** Pages that are in no split, in two splits, or left out on purpose; gaps block saving. */
    coverage: Coverage;
    /** The left-out pages no split covers (what gets saved). */
    leftOut: PageSpan[];
    /** Rows starting or ending past the file's last page, flagged as soon as the page count loads. */
    pastEndRows: number[];
    payload: GtPageSplitInput[];
}

const isStarted = (d: DraftSplit) =>
    d.startPage !== '' ||
    d.endPage !== '' ||
    d.docType !== '' ||
    d.folder !== '';

interface RowCheck {
    errors: string[];
    fieldErrors: Partial<Record<SplitField, string[]>>;
    range: { start: number; end: number } | null;
    pastEnd: boolean;
}

/** Checks one row on its own, so every problem is reported, not just the first. */
function checkRow(
    d: DraftSplit,
    label: string,
    maxPage?: number | null,
): RowCheck {
    const errors: string[] = [];
    const fieldErrors: Partial<Record<SplitField, string[]>> = {};
    const started = isStarted(d);
    // Inline text is page-specific; blanks only show once the row is started.
    const inline = (field: SplitField, message: string) =>
        (fieldErrors[field] ??= []).push(message);
    const fail = (field: SplitField, message: string) => {
        errors.push(`${label}: ${message}`);
        inline(field, message.charAt(0).toUpperCase() + message.slice(1));
    };

    const start = Number(d.startPage);
    const end = Number(d.endPage);
    const startBlank = d.startPage === '';
    const endBlank = d.endPage === '';
    let range: RowCheck['range'] = null;
    let pastEnd = false;

    if (startBlank || endBlank) {
        errors.push(`${label}: start and end pages are required.`);
        if (started && startBlank) inline('startPage', 'Required');
        if (started && endBlank) inline('endPage', 'Required');
    } else {
        const startValid = Number.isInteger(start) && start >= 1;
        const endValid = Number.isInteger(end) && end >= 1;
        if (!startValid) {
            fail('startPage', 'start page must be 1 or greater.');
        } else if (maxPage != null && start > maxPage) {
            pastEnd = true;
            fail(
                'startPage',
                `start page can't exceed ${maxPage} (the file has ${pluralize(maxPage, 'page')}).`,
            );
        }
        if (!endValid) {
            fail('endPage', 'end page must be a whole number, 1 or greater.');
        } else if (startValid && end < start) {
            fail('endPage', 'end page must be ≥ start page.');
        } else if (maxPage != null && end > maxPage) {
            pastEnd = true;
            fail(
                'endPage',
                `end page can't exceed ${maxPage} (the file has ${pluralize(maxPage, 'page')}).`,
            );
        }
        if (startValid && endValid && end >= start) range = { start, end };
    }

    return { errors, fieldErrors, range, pastEnd };
}

/** Carries each saved row's notes onto the row with the same 0-based page range; other rows get none. */
function applySavedNotes(
    payload: GtPageSplitInput[],
    saved: GtPageSplit[],
): GtPageSplitInput[] {
    const notes = new Map(
        saved.map((row) => [`${row.startPage}-${row.endPage}`, row.notes]),
    );
    return payload.map((row) => ({
        ...row,
        notes: notes.get(`${row.startPage}-${row.endPage}`) ?? null,
    }));
}

/** Page-range checks never wait on doc type; a gap blocks saving unless its pages are left out. */
export function analyzeDrafts(
    drafts: DraftSplit[],
    maxPage?: number | null,
    leftOut: PageSpan[] = [],
): SplitAnalysis {
    const errors: string[] = [];
    const errorKeys: string[][] = [];
    const fieldErrors: SplitAnalysis['fieldErrors'] = {};
    const payload: GtPageSplitInput[] = [];
    const ranges: { start: number; end: number; row: number; key: string }[] =
        [];
    const pastEndRows: number[] = [];

    drafts.forEach((d, i) => {
        const check = checkRow(d, `Row ${i + 1}`, maxPage);
        errors.push(...check.errors);
        check.errors.forEach(() => errorKeys.push([d.key]));
        if (Object.keys(check.fieldErrors).length > 0) {
            fieldErrors[d.key] = check.fieldErrors;
        }
        if (check.range) ranges.push({ ...check.range, row: i, key: d.key });
        if (check.pastEnd) pastEndRows.push(i);
        if (check.errors.length === 0 && check.range) {
            // Convert 1-based user-facing pages to 0-based for the wire payload.
            payload.push({
                startPage: check.range.start - 1,
                endPage: check.range.end - 1,
                docType: d.docType || DocType.UNKNOWN_TYPE,
                folder: d.folder || null,
                remark: d.remark.trim() || null,
                checked: d.checked,
            });
        }
    });

    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    // Compare against the row reaching furthest so far, not just the neighbour.
    let widest = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
        const cur = sorted[i];
        if (cur.start <= widest.end) {
            errors.push(
                `Ranges overlap near pages ${widest.start}-${widest.end} and ${cur.start}-${cur.end}.`,
            );
            errorKeys.push([widest.key, cur.key]);
            const widestErrs = (fieldErrors[widest.key] ??= {});
            (widestErrs.endPage ??= []).push(`Overlaps row ${cur.row + 1}`);
            const curErrs = (fieldErrors[cur.key] ??= {});
            (curErrs.startPage ??= []).push(`Overlaps row ${widest.row + 1}`);
        }
        if (cur.end > widest.end) widest = cur;
    }

    const spans = ranges.map((r) => ({ start: r.start, end: r.end }));
    const keptLeftOut = trimLeftOut(leftOut, spans);
    const coverage = checkCoverage(spans, maxPage ?? null, keptLeftOut);
    coverage.gaps.forEach((gap) => {
        const one = gap.start === gap.end;
        errors.push(
            `${formatSpan(gap).replace(/^p/, 'P')} ${one ? 'is' : 'are'} in no document: put ${one ? 'it' : 'them'} in a document or leave ${one ? 'it' : 'them'} out.`,
        );
        errorKeys.push([]);
    });

    return {
        errors,
        errorKeys,
        fieldErrors,
        coverage,
        leftOut: keptLeftOut,
        pastEndRows,
        payload,
    };
}

/**
 * Client-side validation mirroring the server contract (integer pages, start ≤
 * end, a doc type per row, an optional folder, no overlap), plus the file's
 * page count when known (`maxPage`; `null`/`undefined` skips it) and the gaps
 * that are not left out.
 */
export function validateDrafts(
    drafts: DraftSplit[],
    maxPage?: number | null,
    leftOut: PageSpan[] = [],
): {
    errors: string[];
    payload: GtPageSplitInput[];
} {
    const { errors, payload } = analyzeDrafts(drafts, maxPage, leftOut);
    return { errors, payload };
}
